import { useState } from 'react'
import { Link } from 'react-router-dom'

import { env } from 'src/config/env'
import type { StartJourneyButtonBaseProps } from 'src/shared/components/ui/buttons/start-journey-button-base'
import StartJourneyButtonBase from 'src/shared/components/ui/buttons/start-journey-button-base'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { useIsAndroid, useIsAppleDevice } from 'src/shared/hooks/use-device-detect'
import { cn } from 'src/shared/utils/cn'

import { useUserByUsername } from '../../api/accounts/use-user-by-username'

import ProfileStartJourneyPopup from './profile-start-journey-popup'

function DisclaimerShort({ className }: { className?: string }) {
  return (
    <p
      className={cn(
        'mx-auto max-w-[278px] rounded-[15px] text-center text-[11px] leading-[1.6] text-muted xs:text-[13px]',
        className,
      )}
    >
      By clicking “Start my journey”, you accept our{' '}
      <Link to={env.LINK_TO_TERMS_OF_USE} target="_blank" className="underline">
        Terms and Conditions
      </Link>{' '}
      and{' '}
      <Link to={env.LINK_TO_PRIVACY_POLICY} target="_blank" className="underline">
        Privacy Policy
      </Link>
      .
    </p>
  )
}

interface ProfileStartJourneyBtnProps extends StartJourneyButtonBaseProps {
  username: string
}

export default function ProfileStartJourneyBtn({ ...props }: ProfileStartJourneyBtnProps) {
  const { username } = props
  const [isOpen, setIsOpen] = useState(false)
  const { isMobile } = useBreakpoint()
  const isIOS = useIsAppleDevice()
  const isAndroid = useIsAndroid()
  const { data: user } = useUserByUsername(username || '')
  const twilioPhoneNumber = user?.assignedTwilioPhoneNumber

  // If the user is not found or if they don't have a Twilio phone number, don't show the button.
  if (!user || !twilioPhoneNumber) {
    return null
  }

  const firstName = user?.firstName
  const userId = user?.id

  const onClick = () => {
    if (isMobile && (isIOS || isAndroid) && twilioPhoneNumber) {
      const smsNumber = twilioPhoneNumber
      const smsMessage = `Hi Lynk! I'm starting my real estate journey and need help from ${firstName}.`
      // iOS uses '&' separator, Android uses '?' separator
      const separator = isIOS ? '&' : '?'
      const smsUrl = `sms:${smsNumber}${separator}body=${encodeURIComponent(smsMessage)}`
      window.location.href = smsUrl
    } else {
      setIsOpen(true)
    }
  }

  return (
    <div className="flex flex-col items-center">
      <StartJourneyButtonBase {...props} onClick={onClick} />
      <ProfileStartJourneyPopup isOpen={isOpen} setOpen={setIsOpen} userId={userId} />
      {isMobile && <DisclaimerShort className="mt-3" />}
    </div>
  )
}
